import classnames from 'classnames';
import React, { forwardRef, useContext } from 'react';
import styled, { ThemeContext, ThemeProvider } from 'styled-components';
import { InputErrorPositions, InputLabelPositions, InputLooks } from '../../../enums/input.js';
import '../../../themes/alert.js';
import '../../../themes/button.js';
import '../../../themes/checkbox.js';
import '../../../themes/divider.js';
import '../../../themes/dropdown.js';
import '../../../themes/fieldset.js';
import '../../../themes/input.js';
import '../../../themes/list.js';
import '../../../themes/listbox.js';
import '../../../themes/loader.js';
import '../../../themes/option.js';
import '../../../themes/popout.js';
import '../../../themes/switch.js';
import { lightTheme } from '../../../themes/themes.js';
import '../../../themes/tooltip.js';
import '../../atoms/icon/font-icon/font-icon.js';
import 'react-transition-group';
import { InlineAlert } from '../../atoms/alert/inline-alert/inline-alert.js';
import '../../atoms/alert/standard-alert/standard-alert.js';
import 'boring-avatars';
import '../../atoms/tooltip/hover-tooltip/hover-tooltip.js';
import 'react-laag';
import '../../../styles/styled-components/alert.js';
import '../../../styles/styled-components/animations.js';
import '../../../styles/styled-components/box-shadow.js';
import '../../../styles/styled-components/button.js';
import '../../../styles/styled-components/font-face.js';
import '../../../styles/styled-components/global.js';
import '../../../styles/styled-components/input.js';
import '../../../styles/styled-components/list.js';
import '../../../styles/styled-components/listitem.js';
import '../../../styles/styled-components/media-queries.js';
import '../../../styles/styled-components/reset.js';
import '../../../styles/styled-components/outline.js';
import '../../../styles/styled-components/popout.js';
import '../../../styles/styled-components/screen-reader.js';
import '../../../styles/styled-components/switch.js';
import '../../../styles/styled-components/tooltip.js';
import '../../../utilities/color.js';
import 'date-fns';
import '../../../utilities/locale.js';
import '@brandfolder/utilities';
import '../../atoms/button/close-button/close-button.js';
import '../../atoms/button/color-button/color-button.js';
import '../../../hooks/use-prevent-keyboard-scrolling.js';
import '../../atoms/button/icon-button/icon-button.js';
import '../../atoms/button/standard-button/standard-button.js';
import '../../atoms/loader/circle-loader/circle-loader.js';
import '../../atoms/loader/shimmer-loader/shimmer-loader.js';
import '../../atoms/divider/standard-divider/standard-divider.js';
import '../../atoms/fieldset/standard-fieldset/standard-fieldset.js';
import '../../atoms/heading/standard-heading/standard-heading.js';
import '../../atoms/label/standard-label/standard-label.js';
import '../../atoms/link/button-link/button-link.js';
import '../../atoms/link/standard-link/standard-link.js';
import '../../atoms/popout/standard-popout/standard-popout.js';
import '../../atoms/screen-reader/only-for-screen-reader/only-for-screen-reader.js';
import '../../atoms/table/standard-table/caption.js';
import '../../atoms/table/standard-table/tbody.js';
import '../../atoms/table/standard-table/tr-empty.js';
import '../../atoms/table/standard-table/td.js';
import '../../atoms/table/standard-table/tr.js';
import '../../atoms/table/standard-table/table.js';
import '../../atoms/table/standard-table/tfoot.js';
import '../../atoms/table/standard-table/th.js';
import '../../atoms/table/standard-table/thead.js';
import '../../atoms/table/dnd-table/dnd-handle-td.js';
import '../../atoms/table/dnd-table/dnd-table-drag-context.js';
import { HelpText } from '../../atoms/text/help-text/help-text.js';
import '../../atoms/text/standard-text/standard-text.js';
import { TextfieldAddon, TextfieldAddonTypes } from './textfield-addon.js';

const BaseTextfieldBody = /*#__PURE__*/ forwardRef((props, ref)=>{
    const { addonsProps, after, afterProps, before, beforeProps, children, disabled, error, errorPosition, errorProps, helpText, helpTextId, helpTextProps, hidden, inlineAlertId, labelPosition, look, ...otherProps } = props;
    const hasAfter = !!after;
    const hasBefore = !!before;
    const hasError = !!error && !!inlineAlertId;
    const hasHelpText = !!helpText && !!helpTextId;
    const errorBottom = errorPosition === InputErrorPositions.Bottom;
    const errorTop = errorPosition === InputErrorPositions.Top;
    const labelTop = labelPosition === InputLabelPositions.Top;
    const showError = hasError && !hidden;
    return /*#__PURE__*/ React.createElement("div", {
        ...otherProps,
        className: classnames({
            'bf-textfield__body': true,
            'bf-textfield__body--after': hasAfter,
            'bf-textfield__body--before': hasBefore,
            'bf-textfield__body--disabled': disabled,
            'bf-textfield__body--error': hasError,
            'bf-textfield__body--error-bottom': errorBottom,
            'bf-textfield__body--error-top': errorTop,
            'bf-textfield__body--help-text': hasHelpText,
            [`${otherProps?.className}`]: !!otherProps?.className
        }),
        ref: ref
    }, showError && errorTop && !labelTop && /*#__PURE__*/ React.createElement(InlineAlert, {
        ...errorProps,
        id: inlineAlertId
    }, error), /*#__PURE__*/ React.createElement("div", {
        ...addonsProps,
        className: classnames({
            'bf-textfield__addons': true,
            'bf-textfield__addons--after': hasAfter,
            'bf-textfield__addons--before': hasBefore,
            [`${addonsProps?.className}`]: !!addonsProps?.className
        })
    }, hasBefore && /*#__PURE__*/ React.createElement(TextfieldAddon, {
        ...beforeProps,
        disabled: disabled,
        error: error,
        look: look,
        type: TextfieldAddonTypes.Before
    }, before), children, hasAfter && /*#__PURE__*/ React.createElement(TextfieldAddon, {
        ...afterProps,
        disabled: disabled,
        error: error,
        look: look,
        type: TextfieldAddonTypes.After
    }, after)), showError && errorBottom && /*#__PURE__*/ React.createElement(InlineAlert, {
        ...errorProps,
        id: inlineAlertId
    }, error), hasHelpText && !hidden && /*#__PURE__*/ React.createElement(HelpText, {
        ...helpTextProps,
        id: helpTextId
    }, helpText));
});
const Body = styled(BaseTextfieldBody)`
  display: flex;
  flex: 1;
  flex-direction: column;
  position: relative;

  .bf-alert__inline {
    margin-block-end: 5px;
  }

  &.bf-textfield__body--error-bottom {
    .bf-alert__inline {
      margin-block-end: 0;
      margin-block-start: 5px;
    }
  }

  .bf-text__help {
    margin-block-start: 5px;
  }

  .bf-textfield__addons {
    display: flex;
  }
`;
const TextfieldBody = /*#__PURE__*/ forwardRef((props, ref)=>{
    const { errorPosition = InputErrorPositions.Top, labelPosition = InputLabelPositions.Top, look = InputLooks.Default, ...otherProps } = props;
    const themeContext = useContext(ThemeContext);
    return /*#__PURE__*/ React.createElement(ThemeProvider, {
        theme: themeContext || lightTheme
    }, /*#__PURE__*/ React.createElement(Body, {
        ...otherProps,
        errorPosition: errorPosition,
        labelPosition: labelPosition,
        look: look,
        ref: ref
    }));
});

export { TextfieldBody };
//# sourceMappingURL=textfield-body.js.map
